import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import MultiSelectDropdown from '../index';

const MultiSelectSearchWrapper = ({
  title = '',
  searchBlueprint,
  minWidth,
  formatDataFunction = data => data,
  idKey,
  labelKey,
  onChange = () => {},
  selectedValues = [],
  displayLength = 2,
  searchComponent,
  popoverId = undefined
}) => {
  const data = useSelector(searchBlueprint.selectData);
  const pageInfo = useSelector(searchBlueprint.selectPageInfo);
  const dispatch = useDispatch();
  const SearchComponent = searchComponent;

  const formattedData = useMemo(() => formatDataFunction(data), [data]);

  return (
    <Box minWidth={minWidth}>
      <MultiSelectDropdown
        data={formattedData}
        displayLength={displayLength}
        endMessage={(
          <Text color="greyScale.500" mt={2} textAlign="center" textStyle="bodySm">
            {`Showing all (${data.length})`}
          </Text>
        )}
        fetchNextData={() => dispatch(searchBlueprint.getPage({ page: pageInfo.currentPage + 1 }))}
        hasMore={pageInfo.hasMore}
        idKey={idKey}
        labelKey={labelKey}
        minWidth={minWidth}
        pageInfo={pageInfo}
        placeholder={title}
        popoverId={popoverId}
        searchComponent={SearchComponent}
        values={selectedValues}
        onChange={onChange}
      />
    </Box>
  );
};

/** This component is used to select data and page info when using elasticsearch with a multiselect dropdown.
 * */
MultiSelectSearchWrapper.propTypes = {
  /** Placeholder/title that will appear on or above button */
  title: PropTypes.string,
  /** Elasticsearch blueprint to use */
  searchBlueprint: PropTypes.shape({
    getPage: PropTypes.func,
    selectData: PropTypes.func,
    selectPageInfo: PropTypes.func
  }),
  /** If true, the list will include a select all input once all data is loaded */
  selectAllOption: PropTypes.bool,
  /** Width of button and menu */
  minWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Function passed if data needs to be mapped into a different shape */
  formatDataFunction: PropTypes.func,
  /** Key for the value of the input */
  idKey: PropTypes.string,
  /** Key for the text to be displayed next to checkbox */
  labelKey: PropTypes.string,
  /** Function for checking/unchecking input */
  onChange: PropTypes.func,
  /** Checked/selected data */
  selectedValues: PropTypes.arrayOf(PropTypes.shape({})),
  /** How many selected items to display on a button before ellipses. Two is the default for this
   * component because the labels are longer descriptions */
  displayLength: PropTypes.number,
  /** Function for selecting all items from list */
  handleCheckAll: PropTypes.func,
  /** Pass a search component if list should be filterable. For elasticsearch, use SearchGlobalSearchInput */
  searchComponent: PropTypes.func,
  /** Id to target the popover trigger div */
  popoverId: PropTypes.string
};

export default MultiSelectSearchWrapper;
